import { useState } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { apiClient } from '../../services/http.js';
import { useTheme } from '../../hooks/useTheme.jsx';

const VerifyEmailPage = () => {
  const { theme, toggleTheme } = useTheme();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const emailParam = searchParams.get('email') || '';

  const [email, setEmail] = useState(emailParam);
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { res, data } = await apiClient.request('/auth/verify-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code }),
      });
      if (res.ok) {
        setSuccess(true);
        setTimeout(() => navigate('/login'), 2000);
      } else {
        setError(data?.detail || 'Código inválido ou expirado.');
      }
    } catch {
      setError('Erro de conexão.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center relative overflow-hidden bg-background p-4">
      <div className="fixed top-[-20%] left-[-10%] w-[60vw] h-[60vw] rounded-full bg-primary-container/5 blur-[120px] pointer-events-none"></div>
      <div className="fixed bottom-[-20%] right-[-10%] w-[50vw] h-[50vw] rounded-full bg-primary-container/5 blur-[100px] pointer-events-none"></div>

      <div className="fixed top-4 left-4 right-4 flex justify-between z-20">
        <button onClick={() => navigate('/')} className="flex items-center gap-2 text-on-surface-variant hover:text-primary transition-colors text-sm">
          <span className="material-symbols-outlined text-[20px]">arrow_back</span>
          <span className="hidden sm:inline">Início</span>
        </button>
        <button onClick={toggleTheme} className="p-2 rounded-full text-on-surface-variant hover:text-primary hover:bg-primary/10 transition-all">
          <span className="material-symbols-outlined text-[20px]">{theme === 'dark' ? 'light_mode' : 'dark_mode'}</span>
        </button>
      </div>

      <main className="relative z-10 w-full max-w-md px-6 py-12 flex flex-col items-center">
        <div className="mb-8 flex flex-col items-center opacity-80">
          <img alt="CatholicGPT Logo" className="h-16 w-16 mx-auto rounded-full bg-white object-contain p-2 shadow-sm mb-4" src="/logo-192.png"/>
        </div>

        <div className="w-full glass-panel rounded-xl border border-outline-variant/20 shadow-2xl p-8">
          <h2 className="font-headline text-2xl font-bold text-on-surface mb-2 text-center">Verificar Email</h2>
          <p className="text-on-surface-variant text-sm text-center mb-6">
            Digite o código de 6 dígitos enviado para seu email
          </p>

          {success ? (
            <div className="text-center py-4">
              <span className="material-symbols-outlined text-primary text-5xl mb-3 block">check_circle</span>
              <p className="text-primary font-medium mb-1">Email verificado com sucesso!</p>
              <p className="text-on-surface-variant text-sm">Redirecionando para o login...</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              <div className="relative">
                <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-on-surface-variant opacity-60 text-lg">mail</span>
                <input
                  className="w-full bg-surface-container-lowest border-0 border-b border-outline-variant/30 pl-10 pr-4 py-3 text-on-surface outline-none focus:border-primary transition-colors"
                  placeholder="Email" required type="email"
                  autoComplete="email"
                  value={email} onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <input
                className="w-full bg-surface-container-lowest border-0 border-b border-outline-variant/30 px-4 py-3 text-on-surface outline-none focus:border-primary text-center text-lg tracking-widest transition-colors"
                placeholder="000000" required type="text" maxLength={6}
                autoComplete="one-time-code"
                value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              />
              {error && <p className="text-error text-sm text-center">{error}</p>}
              <button
                className="w-full bg-primary-container hover:bg-primary text-on-primary font-medium py-3 rounded-full mt-2 transition-all disabled:opacity-50"
                type="submit" disabled={loading || code.length !== 6}
              >
                {loading ? 'Verificando...' : 'Confirmar'}
              </button>
            </form>
          )}

          <div className="mt-6 text-center">
            <p className="text-sm text-on-surface-variant">
              Não recebeu?{' '}
              <Link className="text-primary font-medium" to="/register">Cadastrar novamente</Link>
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default VerifyEmailPage;
